import React from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import { AbstractMedia } from 'types/AbstractMedia';
import { isLowPerfMode } from 'modules/app/selectors';
import { useSlideRotator } from 'hooks/useSlideRotator';
import { getScreensaverMedia, getScreensaverPeriod } from 'modules/dealers/selectors';
import { safePlay } from 'utils/mediaUtils';

import Slide from './Slide';

import './index.css';

type ScreensaverProps = { onClose?: () => void };

const ANIMATION_DURATION_MS = 450;
const SLIDE_GAP_MS = 200;

const Screensaver: React.FC<ScreensaverProps> = ({ onClose }) => {
  const swipperRef = React.useRef<HTMLDivElement>(null);

  const delay = useSelector(getScreensaverPeriod);
  const mediaList = useSelector(getScreensaverMedia);
  const lowPerfMode = useSelector(isLowPerfMode);

  const onChange = React.useCallback((_index: number, media: AbstractMedia | undefined) => {
    if (!media?.id || !swipperRef.current) {
      return;
    }
    const videoComponent = swipperRef.current.querySelector(`div[data-slide-id="${media?.id}"] video`) as HTMLVideoElement | null;
    if (videoComponent) {
      videoComponent.currentTime = 0;
      safePlay(videoComponent);
    } else {
      // In case the component is not yet mounted, we retry after a short delay
      setTimeout(() => onChange(_index, media), 100);
    }
  }, []);

  const { activeMedia, nextMedia, duration } = useSlideRotator(mediaList, { defaultDuration: delay, onChange });

  React.useEffect(() => {
    // We need to remove the `no-transition` class for the animations to work
    if (lowPerfMode) {
      document.body.classList.remove('no-transition');
      return () => document.body.classList.add('no-transition');
    }
  }, [lowPerfMode]);

  return (
    <div className="swiper" ref={swipperRef}>
      <Slide
        key={activeMedia.id}
        media={activeMedia}
        onClose={onClose}
        className={classNames('swiper-slide', mediaList.length > 1 && 'swiper-slide-active')}
        style={{
          '--screensaver-animation-duration': `${ANIMATION_DURATION_MS}ms`,
          '--screensaver-animation-out-delay': `${duration - ANIMATION_DURATION_MS - SLIDE_GAP_MS}ms`
        }}
      />
      {activeMedia !== nextMedia && (
        <Slide key={nextMedia.id} media={nextMedia} style={{ opacity: 0 }} />
      )}
    </div>
  );
};

export default Screensaver;
